/**
 * Represents a class constructor type.
 */
export type ClassInstance = new (...args: any[]) => void

/**
 * Generates an error message when a value is not an instance of the specified class.
 * 
 * @param guardName - The name of the variable being guarded.
 * @param guardType - The expected class constructor.
 * @returns A formatted error message.
 */
export const notInstanceMessage = (guardName: string, guardType: ClassInstance): string => (
  `'${guardName}' must be an instance of '${guardType.name}'`
)

/**
 * Throws an error if the provided value is not an instance of the specified class.
 * 
 * @param guardName - The name of the variable being guarded.
 * @param guard - The value to check.
 * @param guardType - The expected class constructor.
 * @throws {Error} Throws an error if `guard instanceof guardType` is false.
 */
export const throwNotInstance = (guardName: string, guard: any, guardType: ClassInstance): void => {
  const isInstance = guard instanceof guardType;
  if (isInstance === false) {
    throw new Error(notInstanceMessage(guardName, guardType));
  }
}

/**
 * Generates an error message when a value is not an instance of any of the specified classes.
 * 
 * @param guardInstance - The value that failed the check.
 * @param guardTypes - The expected class constructors.
 * @returns A formatted error message.
 */
export const notAnyInstanceMessage = (guardInstance: any, ...guardTypes: ClassInstance[]): string => {
  const typeNames = guardTypes.map(t => t.name).join('|');
  return `'${guardInstance.constructor.name}' must be an instance of either '${typeNames}'`
}

/**
 * Throws an error if the provided value is not an instance of any of the specified classes.
 * 
 * @param guardInstance - The value to check.
 * @param guardTypes - One or more expected class constructors.
 * @throws {Error} Throws an error if `guardInstance` is not an instance of any of the `guardTypes`.
 */
export const throwNotAnyInstance = (guardInstance: any, ...guardTypes: ClassInstance[]): void => {
  for (let i = 0; i < guardTypes.length; i++) {
    const isInstance = guardInstance instanceof guardTypes[i];
    if (isInstance) return;
  }
  throw new Error(notAnyInstanceMessage(guardInstance, ...guardTypes));
}